import { toast } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { QueryKeys } from "@/constants/Query";
import { useQueryClient } from "@tanstack/react-query";
import {
  type AVCallSuggestedCondition as AVCallSuggestedConditionType,
  type AVCallSuggestedConditionsData,
  type AVCallSuggestedSnomedCode,
  useBatchAddMedicalHistory,
  useMarkAVCallConditionAddedToChart,
} from "../../network";
import { AVCallConditionSearch } from "./AVCallConditionSearch";
import { AVCallSuggestedConditionPopover } from "./AVCallSuggestedConditionPopover";
import { AVCallSuggestedConditionRow } from "./AVCallSuggestedConditionRow";

interface AVCallSuggestedConditionProps {
  condition: AVCallSuggestedConditionType;
  patientId: string;
  encounterId: string;
}

/**
 * Orchestrates a single AV-call suggested condition row. Picks the right
 * sub-component based on how many SNOMED candidates the extractor produced:
 *   - 0 candidates → manual SNOMED search (`AVCallConditionSearch`)
 *   - 1 candidate  → single Add row (`AVCallSuggestedConditionRow`)
 *   - 2+ candidates → dropdown picker (`AVCallSuggestedConditionPopover`)
 *
 * Add flow: create the FHIR EpisodeOfCare/Condition via
 * `batchAddMedicalHistory`, optimistically patch the cached query so the row
 * flips to "Added" instantly, then best-effort mark the suggestion on the
 * Dynamo extraction record so the Added state survives reloads.
 */
export const AVCallSuggestedCondition = ({
  condition,
  patientId,
  encounterId,
}: AVCallSuggestedConditionProps) => {
  const queryClient = useQueryClient();

  // Derived from the cached query data — not local state — so socket-driven
  // invalidations / cross-session updates land here on the next refetch
  // without a remount, and we avoid the unmounted-setState pitfall after
  // awaiting an async mutation.
  const addedCode = condition.snomedCodes?.find((c) => c.addedToChartAt);

  const {
    mutateAsync: batchAddMedicalHistory,
    isPending,
    variables,
  } = useBatchAddMedicalHistory({ patientId });

  const { mutateAsync: markConditionAddedToChart } =
    useMarkAVCallConditionAddedToChart({ patientId, encounterId });

  const pendingCodeDisplay = isPending
    ? variables?.conditionCodes?.[0]?.text
    : undefined;

  const singleCode =
    condition.snomedCodes?.length === 1 ? condition.snomedCodes[0] : null;
  const noSnomedCodes = !condition.snomedCodes?.length;

  const handleAdd = async (snomedCode: AVCallSuggestedSnomedCode) => {
    if (!snomedCode.code || !snomedCode.system) {
      toast.error("Missing SNOMED code or system; cannot add.");
      return;
    }
    try {
      await batchAddMedicalHistory({
        patientId,
        conditionCodes: [
          {
            coding: [
              {
                code: snomedCode.code,
                display: snomedCode.display ?? undefined,
                system: snomedCode.system,
              },
            ],
            text: snomedCode.display ?? undefined,
          },
        ],
      });
    } catch {
      toast.error(
        `Failed to add ${snomedCode.display ?? "condition"} to medical history`,
      );
      return;
    }

    // Optimistic local patch: flip the row to "Added" instantly without
    // waiting for the mark-as-added mutation + invalidation + refetch
    // round-trip. The refetch triggered by `useMarkAVCallConditionAddedToChart`
    // onSuccess will overwrite this with the authoritative server state.
    const nowIso = new Date().toISOString();
    queryClient.setQueryData<AVCallSuggestedConditionsData>(
      QueryKeys.Patient.Chart.AVCallConditions.DEFAULT({
        patientId,
        encounterId,
      }),
      (old) => {
        if (!old) return old;
        const patch = (c: AVCallSuggestedConditionType) => {
          const alreadyPresent = c.snomedCodes.some(
            (s) => s.code === snomedCode.code && s.system === snomedCode.system,
          );
          if (alreadyPresent) {
            return {
              ...c,
              snomedCodes: c.snomedCodes.map((s) =>
                s.code === snomedCode.code && s.system === snomedCode.system
                  ? { ...s, addedToChartAt: nowIso }
                  : s,
              ),
            };
          }
          // Search case: this row had no candidates and the user picked a
          // brand-new SNOMED via `AVCallConditionSearch`. Append it so
          // `addedCode` (derived from `snomedCodes`) flips to "Added"
          // immediately. Scope by `sourceId` to avoid leaking the chosen
          // code into sibling rows.
          if (c.sourceId && c.sourceId === condition.sourceId) {
            return {
              ...c,
              snomedCodes: [
                ...c.snomedCodes,
                {
                  code: snomedCode.code,
                  display: snomedCode.display ?? null,
                  system: snomedCode.system,
                  addedToChartAt: nowIso,
                },
              ],
            };
          }
          return c;
        };
        return {
          ...old,
          historicalConditions: old.historicalConditions.map(patch),
          rfeConditions: old.rfeConditions.map(patch),
        };
      },
    );

    // Best-effort: persist acceptance on the Dynamo row so the Added state
    // survives reloads. EOC is already on the chart even if this fails — the
    // user can re-trigger from a future session if needed.
    try {
      await markConditionAddedToChart({
        encounterId,
        conditionCode: snomedCode.code,
        conditionCodeSystem: snomedCode.system,
      });
    } catch {}
  };

  const termDisplay = (condition.term ?? "").replace(/\b\w/g, (c) =>
    c.toUpperCase(),
  );

  return (
    <div className="border-l-4 border-ai pl-2 py-0.5 rounded">
      <BodyText size="sm" bold>
        {termDisplay || "—"}
      </BodyText>
      {noSnomedCodes ? (
        <AVCallConditionSearch
          patientId={patientId}
          defaultSearchTerm={condition.term ?? ""}
          onAdd={handleAdd}
          isPending={isPending}
          addedCode={addedCode}
          pendingCodeDisplay={pendingCodeDisplay}
        />
      ) : singleCode ? (
        <AVCallSuggestedConditionRow
          snomedCode={singleCode}
          onAdd={handleAdd}
          isPending={isPending}
          isAdded={addedCode?.code === singleCode.code}
        />
      ) : (
        <AVCallSuggestedConditionPopover
          snomedCodes={condition.snomedCodes}
          onAdd={handleAdd}
          isPending={isPending}
          addedCode={addedCode}
          pendingCodeDisplay={pendingCodeDisplay}
        />
      )}
    </div>
  );
};
